import React, { useState, useEffect } from 'react';
import { Mail, Plus, X, Edit2, Save, Info } from 'lucide-react';
import { getEmailRecipients, saveEmailRecipients } from '../services/api';

function EmailRecipientsForm() {
  const [recipients, setRecipients] = useState([]);
  const [lastUpdatedBy, setLastUpdatedBy] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [editableRecipients, setEditableRecipients] = useState([]);

  useEffect(() => {
    loadRecipients();
  }, []);

  const loadRecipients = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const data = await getEmailRecipients();
      setRecipients(data.recipients || []);
      setLastUpdatedBy(data.lastUpdatedBy || null);
      setUpdatedAt(data.updatedAt || null);
    } catch (err) {
      setLoadError(true);
      setFeedback({ type: 'error', message: 'Failed to load email recipients' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditableRecipients([...recipients]);
    setEditing(true);
    setFeedback(null);
  };

  const handleCancel = () => {
    setEditing(false);
    setEditableRecipients([]);
    setNewEmail('');
    setFeedback(null);
  };

  const handleAddEmail = () => {
    const email = newEmail.trim();
    if (!email) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setFeedback({ type: 'error', message: 'Please enter a valid email address' });
      return;
    }
    if (editableRecipients.includes(email)) {
      setFeedback({ type: 'error', message: 'This email is already in the list' });
      return;
    }
    setEditableRecipients([...editableRecipients, email]);
    setNewEmail('');
    setFeedback(null);
  };

  const handleRemoveEmail = (email) => {
    setEditableRecipients(editableRecipients.filter(e => e !== email));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const result = await saveEmailRecipients(editableRecipients);
      setRecipients(editableRecipients);
      setLastUpdatedBy(result.lastUpdatedBy);
      setUpdatedAt(result.updatedAt);
      setEditing(false);
      setFeedback({ type: 'success', message: 'Email recipients updated successfully.' });
      setTimeout(() => setFeedback(null), 4000);
    } catch (err) {
      setFeedback({ type: 'error', message: `Failed to save: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading email recipients...</div>;

  return (
    <div className="email-recipients-container">
      <div className="email-recipients-header">
        <h3><Mail size={18} /> Bug Report Email Recipients</h3>
        <div className="email-recipients-info">
          <Info size={14} />
          <span>This is a shared list used by all members. Anyone can edit it.</span>
        </div>
      </div>

      {feedback && (
        <div className={`settings-feedback ${feedback.type === 'success' ? 'settings-feedback-success' : 'settings-feedback-error'}`} role="status">
          {feedback.message}
        </div>
      )}

      {!editing ? (
        <div className="email-recipients-view">
          {recipients.length === 0 ? (
            <p className="email-recipients-empty">No email recipients configured. Click Edit to add recipients for the bug dashboard email report.</p>
          ) : (
            <div className="email-recipients-list">
              {recipients.map((email) => (
                <span key={email} className="email-chip">{email}</span>
              ))}
            </div>
          )}
          {lastUpdatedBy && (
            <p className="email-recipients-meta">
              Last updated by <strong>{lastUpdatedBy}</strong> on {new Date(updatedAt).toLocaleString('en-US', { timeZone: 'America/Chicago' })}
            </p>
          )}
          <button className="btn btn-secondary btn-sm" onClick={handleEdit} disabled={loadError}>
            <Edit2 size={14} /> Edit
          </button>
        </div>
      ) : (
        <div className="email-recipients-edit">
          <div className="email-recipients-list">
            {editableRecipients.map((email) => (
              <span key={email} className="email-chip email-chip-editable">
                {email}
                <button className="email-chip-remove" onClick={() => handleRemoveEmail(email)} title="Remove" aria-label={`Remove ${email}`}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
          <div className="email-add-row">
            <input
              type="email"
              className="input"
              placeholder="Add email address..."
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button className="btn btn-secondary btn-sm" onClick={handleAddEmail} type="button">
              <Plus size={14} /> Add
            </button>
          </div>
          <div className="email-recipients-actions">
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              <Save size={14} /> {saving ? 'Saving...' : 'Save'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={handleCancel} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailRecipientsForm;
